import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  TextField,
  Button,
  Alert,
  Paper,
  Snackbar,
} from '@mui/material';
import HeroSection from '../components/common/HeroSection';
import SectionTitle from '../components/common/SectionTitle';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SendIcon from '@mui/icons-material/Send';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    if (!formData.message.trim()) newErrors.message = 'Message is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field) => (e) => {
    setFormData({ ...formData, [field]: e.target.value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    // For now, open mailto with form data. Backend integration later.
    const subject = `Enquiry from ${formData.name}`;
    const body = `Name: ${formData.name}\nEmail: ${formData.email}\nPhone: ${formData.phone}\n\nMessage:\n${formData.message}`;
    window.location.href = `mailto:xinmo688@gmail.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

    setSnackbar({
      open: true,
      message: 'Opening your email client... If nothing happens, please use WhatsApp or call us directly.',
      severity: 'info',
    });
  };

  const handleWhatsAppClick = () => {
    const message = formData.message
      ? `Hi, my name is ${formData.name || 'a parent'}. ${formData.message}`
      : 'Hello! I would like to enquire about tuition classes.';
    window.open(`https://wa.me/6596873957?text=${encodeURIComponent(message)}`, '_blank');
  };

  const contactInfo = [
    {
      icon: <LocationOnIcon sx={{ fontSize: 28 }} />,
      title: 'Address',
      details: ['Canberra Community Club', '2 Sembawang Crescent #03-03C', 'Singapore 757632'],
    },
    {
      icon: <PhoneIcon sx={{ fontSize: 28 }} />,
      title: 'Phone',
      details: ['+65 9687 3957'],
    },
    {
      icon: <EmailIcon sx={{ fontSize: 28 }} />,
      title: 'Email',
      details: ['xinmo688@gmail.com'],
    },
    {
      icon: <AccessTimeIcon sx={{ fontSize: 28 }} />,
      title: 'Operating Hours',
      details: ['Weekdays: 3pm - 9:30pm', 'Weekends: 9am - 7pm'],
    },
  ];

  return (
    <Box>
      <HeroSection
        title="Contact Us"
        subtitle="Get in touch to enroll or learn more about our classes"
      />

      <Box sx={{ py: { xs: 6, md: 10 }, bgcolor: 'background.default' }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {/* Contact Form */}
            <Grid item xs={12} md={7}>
              <Card sx={{ p: { xs: 3, md: 4 } }}>
                <SectionTitle title="Send Us a Message" align="left" />
                <form onSubmit={handleSubmit}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Your Name"
                        value={formData.name}
                        onChange={handleChange('name')}
                        error={!!errors.name}
                        helperText={errors.name}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Your Email"
                        value={formData.email}
                        onChange={handleChange('email')}
                        error={!!errors.email}
                        helperText={errors.email}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Phone Number (optional)"
                        value={formData.phone}
                        onChange={handleChange('phone')}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Your Message"
                        multiline
                        rows={5}
                        value={formData.message}
                        onChange={handleChange('message')}
                        error={!!errors.message}
                        helperText={errors.message}
                        variant="outlined"
                        placeholder="e.g. I'd like to enroll my child in P3 Chinese class..."
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <Button
                          type="submit"
                          variant="contained"
                          size="large"
                          startIcon={<SendIcon />}
                        >
                          Send via Email
                        </Button>
                        <Button
                          variant="contained"
                          size="large"
                          onClick={handleWhatsAppClick}
                          startIcon={<WhatsAppIcon />}
                          sx={{
                            bgcolor: '#25D366',
                            '&:hover': { bgcolor: '#1DA851' },
                            boxShadow: '0 4px 14px rgba(37,211,102,0.35)',
                          }}
                        >
                          WhatsApp Us
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </form>
              </Card>
            </Grid>

            {/* Contact Info Sidebar */}
            <Grid item xs={12} md={5}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {contactInfo.map((info, index) => (
                  <Paper key={index} sx={{ p: 3, borderRadius: 3 }}>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Box
                        sx={{
                          width: 50,
                          height: 50,
                          borderRadius: 2,
                          bgcolor: 'primary.main',
                          color: '#fff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        {info.icon}
                      </Box>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                          {info.title}
                        </Typography>
                        {info.details.map((detail, idx) => (
                          <Typography key={idx} variant="body2" color="text.secondary">
                            {detail}
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  </Paper>
                ))}
              </Box>

              {/* Map */}
              <Paper sx={{ mt: 2, borderRadius: 3, overflow: 'hidden' }}>
                <iframe
                  title="Location Map"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3988.5835!2d103.8270!3d1.4450!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x31da145a5f53a0c1%3A0x5c0e3c5e5e5e5e5e!2sCanberra%20Community%20Club!5e0!3m2!1sen!2ssg!4v1700000000000!5m2!1sen!2ssg"
                  width="100%"
                  height="200"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ContactPage;
